import { Routes } from 'config/routes';
import { DonorStatus } from 'gql/types';

import { DateRangeType } from '../types/phlebotomist-dashboard';

export interface IOpsDrilldownParams {
  dataTable: string;
  locationId?: string;
  locationName?: string;
  dateRange?: DateRangeType;
  customDateRange?: { start: Date; end: Date };
  regionId?: string;
  recruiterId?: string;
  activeStatuses?: DonorStatus[];
  daysInPeriod?: number;
}

/**
 * Metadata for each drilldown table type.
 */
export const OPS_DRILLDOWN_META: Record<
  string,
  { title: string; description: string; iconColor: string }
> = {
  allLocationDonors: {
    title: 'All Location Donors',
    description: 'All donors at this location, regardless of status.',
    iconColor: '#1565c0',
  },
  activeDonors: {
    title: 'Active Donors',
    description: 'Currently active donors at this location.',
    iconColor: '#52c41a',
  },
  recruited: {
    title: 'Recruited Donors',
    description: 'Donors recruited during the selected period.',
    iconColor: '#0e4d8f',
  },
  'summary:recruited': {
    title: 'Recruited Donors (All Locations)',
    description:
      'All donors recruited during the selected period across all locations.',
    iconColor: '#0e4d8f',
  },
  'summary:units': {
    title: 'Units by Location',
    description:
      'Units collected during the selected period, broken down by location.',
    iconColor: '#f31a25',
  },
  pending: {
    title: 'Pending Donors',
    description: 'Donors currently in pending status at this location.',
    iconColor: '#faad14',
  },
  newRecruits: {
    title: 'New Recruits',
    description: 'Newly recruited donors during the selected period.',
    iconColor: '#0e4d8f',
  },
  retired: {
    title: 'Retired Donors',
    description: 'Donors who retired during the selected period.',
    iconColor: '#8c8c8c',
  },
  // Health flags
  newRegistration: {
    title: 'New Registration Flag — Donors',
    description:
      'Donors at this location, sorted by registration date. Flag tracks days since last new registration.',
    iconColor: '#1890ff',
  },
  pipelineStall: {
    title: 'Pipeline Stall Flag — Pending Donors',
    description:
      'Pending donors at this location. Flag tracks average days in pending status.',
    iconColor: '#faad14',
  },
  potentialReg: {
    title: 'Potential Registration Flag — Active Donors',
    description:
      'Active donors at this location. Flag tracks the ratio of active donors to the estimated donor pool.',
    iconColor: '#52c41a',
  },
  unitMom: {
    title: 'Unit MoM Flag — Donors with Donations',
    description:
      'Donors with donations in the current period. Flag tracks month-over-month unit change.',
    iconColor: '#f31a25',
  },
  locationUnits: {
    title: 'Units / Event — Donor Donations',
    description:
      'Donations that produced pRBC units at this location during the selected period.',
    iconColor: '#f31a25',
  },
  // Retiring donors (age-based)
  retiringIn30: {
    title: 'Retiring in 30 Days',
    description:
      'Active donors at this location who will age out of eligibility within 30 days.',
    iconColor: '#e8008a',
  },
  retiringIn60: {
    title: 'Retiring in 60 Days',
    description:
      'Active donors at this location who will age out of eligibility within 60 days.',
    iconColor: '#faad14',
  },
  retiringIn90: {
    title: 'Retiring in 90 Days',
    description:
      'Active donors at this location who will age out of eligibility within 90 days.',
    iconColor: '#52c41a',
  },
  // Pipeline timing
  appliedToActive: {
    title: 'Applied → Active Pipeline',
    description:
      'Donors who transitioned from Applied to Active status. Shows time spent in Applied status.',
    iconColor: '#1890ff',
  },
  pendingToApproved: {
    title: 'Pending → Approved Pipeline',
    description:
      'Donors who transitioned from Pending to Approved status. Shows time spent in Pending status.',
    iconColor: '#52c41a',
  },
  activeToRetired: {
    title: 'Active → Retired Pipeline',
    description:
      'Donors who transitioned from Active to Retired status. Shows time spent in Active status.',
    iconColor: '#8c8c8c',
  },
  pendingToArchived: {
    title: 'Pending → Archived Pipeline',
    description:
      'Donors who transitioned from Pending to Archived status. Shows time spent before archival.',
    iconColor: '#8c8c8c',
  },
  // Overview tab metrics
  'overview:unitsShipped': {
    title: 'Monthly Units Shipped',
    description:
      'Units shipped per location and product type during the selected period.',
    iconColor: '#4B9EFF',
  },
  'overview:wastage': {
    title: 'Wastage / Discard Rate',
    description:
      'Discarded units by reason code and location during the selected period.',
    iconColor: '#E8008A',
  },
  'overview:turnover': {
    title: 'Inventory Turnover',
    description:
      'Units in vs units out per location during the selected period.',
    iconColor: '#F7A500',
  },
  'overview:fillRate': {
    title: 'Reservation Fill Rate',
    description:
      'Recurring reservation template vs actual fulfilled, by client.',
    iconColor: '#00E5A0',
  },
  'overview:sales': {
    title: 'WooCommerce Sales Trends',
    description:
      'Orders over time with product mix and location split during the selected period.',
    iconColor: '#5B2D8E',
  },
};

/**
 * Build the URL for the Ops Drilldown tab with query params encoding the
 * current filters and the metric to drill into.
 */
export function buildOpsDrilldownUrl(params: IOpsDrilldownParams): string {
  const sp = new URLSearchParams();

  sp.set('dataTable', params.dataTable);

  if (params.locationId) sp.set('locationId', params.locationId);
  if (params.locationName) sp.set('locationName', params.locationName);

  if (params.dateRange) sp.set('dateRange', params.dateRange);
  if (params.dateRange === 'custom' && params.customDateRange) {
    sp.set('customStart', params.customDateRange.start.toISOString());
    sp.set('customEnd', params.customDateRange.end.toISOString());
  }

  if (params.regionId) sp.set('regionId', params.regionId);
  if (params.recruiterId) sp.set('recruiterId', params.recruiterId);

  if (params.activeStatuses && params.activeStatuses.length > 0) {
    sp.set('activeStatuses', params.activeStatuses.join(','));
  }

  if (params.daysInPeriod !== undefined) {
    sp.set('daysInPeriod', String(params.daysInPeriod));
  }

  return `${Routes.DashboardsOperationsDrilldown}?${sp.toString()}`;
}
